import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../contexts/UserContext';
import { useCounters } from '../../contexts/CounterContext';
import { useCopyUrl } from '../../hooks/useCopyUrl';
import { Counter } from '../../types';
import { useToast } from '../../utils/toastUtils';
import { extractIdFromUrl } from '../../utils/urlUtils';

// Add TypeScript declaration for Bootstrap
declare global {
  interface Window {
    bootstrap: any;
  }
}

interface SubNavbarProps {
  counterId: string;
  isOwnerUrl: boolean;
}

const SubNavbar: React.FC<SubNavbarProps> = ({ counterId, isOwnerUrl }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, hasCounterAccess, isLoggedIn } = useUser();
  const { counters } = useCounters();
  const { copyUrl, getCounterUrl, getPublicUrl, getOwnerUrl } = useCopyUrl();
  const { showToast } = useToast();
  
  // Store the current counter in state to prevent rendering issues
  const [currentCounter, setCurrentCounter] = useState<Counter | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  
  // Create unique ID for this subnav collapse to avoid conflicts with the main navbar
  const subnavCollapseId = `subnavCollapse_${counterId}`;
  
  // For the enhanced mobile experience
  const [isNavCollapsed, setIsNavCollapsed] = useState(true);
  const collapseRef = useRef<HTMLDivElement>(null);
  
  // Add state for copy feedback
  const [ownerCopyFeedback, setOwnerCopyFeedback] = useState(false);
  const [publicCopyFeedback, setPublicCopyFeedback] = useState(false);
  
  // Update the current counter when props or counters change
  useEffect(() => {
    // Set loading state initially
    setIsLoading(true);
    
    if (counters.length === 0) {
      // Keep loading state if counters haven't been fetched yet
      return;
    }
    
    let foundCounter: Counter | undefined;
    
    if (isOwnerUrl) {
      foundCounter = counters.find(c => c.ownerUrl === counterId);
    } else {
      foundCounter = counters.find(c => c._id === counterId);
    }
    
    setCurrentCounter(foundCounter);
    setIsLoading(false);
  }, [counterId, isOwnerUrl, counters]);
  
  const handleToggleClick = () => {
    setIsNavCollapsed(!isNavCollapsed);
    if (typeof window !== 'undefined' && window.bootstrap) {
      const collapseElement = document.getElementById(subnavCollapseId);
      if (collapseElement) {
        const bsCollapse = window.bootstrap.Collapse.getOrCreateInstance(collapseElement);
        bsCollapse.toggle();
      }
    }
  };
  
  // Close menu when navigating to a new page
  useEffect(() => {
    setIsNavCollapsed(true);
    // Use Bootstrap's native method to close collapse when navigating
    if (typeof window !== 'undefined' && window.bootstrap) {
      const collapseElement = document.getElementById(subnavCollapseId);
      if (collapseElement && window.bootstrap.Collapse) {
        const bsCollapse = window.bootstrap.Collapse.getOrCreateInstance(collapseElement);
        if (bsCollapse) {
          bsCollapse.hide();
        }
      }
    }
  }, [pathname, subnavCollapseId]);

  // Initialize bootstrap collapse when component mounts
  useEffect(() => {
    // Ensure Bootstrap is initialized
    if (typeof window !== 'undefined' && window.bootstrap) {
      const collapseElement = document.getElementById(subnavCollapseId);
      if (collapseElement) {
        // Initialize Bootstrap collapse
        window.bootstrap.Collapse.getOrCreateInstance(collapseElement, {
          toggle: false
        });
      }
    }
  }, [subnavCollapseId, currentCounter]);
  
  // Handle copy with feedback
  const handleCopy = (url: string, isOwner: boolean) => {
    copyUrl(url, isOwner);
    if (isOwner) {
      setOwnerCopyFeedback(true);
      setTimeout(() => setOwnerCopyFeedback(false), 2000);
    } else {
      setPublicCopyFeedback(true);
      setTimeout(() => setPublicCopyFeedback(false), 2000);
    }
  };
  
  // Show loading state if still loading or no counter found
  if (isLoading || !currentCounter) {
    return (
      <div className="bg-body-tertiary py-2 shadow-sm">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0 text-primary">{isLoading ? 'Loading...' : 'Counter Not Found'}</h5>
            {isLoading && (
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  const counterName = currentCounter?.name || 'Counter';

  // Create the correct base URL path
  const basePath = `/${counterId}`;
  
  // Check if we're on each view
  const isCounterView = pathname === basePath;
  const isButtonsView = pathname === `${basePath}/buttons`;
  const isHistoryView = pathname === `${basePath}/history`;
  const isCounterSettingsView = pathname === `${basePath}/counter-settings`;
  const isStatisticsView = pathname === `${basePath}/statistics`;
  
  // URLs for sharing
  const publicUrl = getPublicUrl(currentCounter._id);
  const ownerUrl = getOwnerUrl(currentCounter.ownerUrl);

  // Check management access for this counter
  const hasManageAccess = hasCounterAccess(
    currentCounter.ownerEmail || null, 
    isOwnerUrl, 
    'manage'
  );

  // Check view access for this counter
  const hasViewAccess = hasCounterAccess(
    currentCounter.ownerEmail || null, 
    isOwnerUrl, 
    'view'
  );

  const closeMenu = () => {
    setIsNavCollapsed(true);
  };

  // If no view access, don't render the navbar
  if (!hasViewAccess) {
    return null;
  }

  return (
    <>
      <nav className="navbar navbar-expand-md border-bottom shadow-sm py-2">
        <div className="container">
          <div className="navbar-brand d-flex align-items-center fw-bold">
            <span className="me-2">{currentCounter.name || 'Counter'}</span>
            {isLoading ? (
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">{t('common.loading')}</span>
              </div>
            ) : null}
          </div>

          <button
            className="navbar-toggler border-0 p-1"
            type="button"
            onClick={handleToggleClick}
            aria-controls="subNavbarNav"
            aria-expanded={!isNavCollapsed}
            aria-label={t('navbar.toggleNavigationAriaLabel')}
          >
            <i className="bi bi-three-dots-vertical"></i>
          </button>

          <div
            className={`collapse navbar-collapse ${isNavCollapsed ? '' : 'show'}`}
            id="subNavbarNav"
            ref={collapseRef}
          >
            <ul className="navbar-nav ms-auto">
              {/* Counter View Link - Always visible */}
              <li className="nav-item">
                <Link
                  className={`nav-link px-3 py-2 ${isCounterView ? 'active bg-primary text-white rounded' : ''}`}
                  to={basePath}
                  onClick={closeMenu}
                >
                  <i className="bi bi-hand-index me-1"></i> {t('counter.view')}
                </Link>
              </li>

              {/* Buttons Link - Only for users with management access */}
              {hasManageAccess && (
                <li className="nav-item">
                  <Link
                    className={`nav-link px-3 py-2 ${isButtonsView ? 'active bg-primary text-white rounded' : ''}`}
                    to={`${basePath}/buttons`}
                    onClick={closeMenu}
                  >
                    <i className="bi bi-bullseye me-1"></i> {t('buttons.title')}
                  </Link>
                </li>
              )}

              {/* Statistics Link - Always visible */}
              <li className="nav-item">
                <Link
                  className={`nav-link px-3 py-2 ${isStatisticsView ? 'active bg-primary text-white rounded' : ''}`}
                  to={`${basePath}/statistics`}
                  onClick={closeMenu}
                >
                  <i className="bi bi-table me-1"></i> {t('statistics.title')}
                </Link>
              </li>

              {/* History Link - Always visible */}
              <li className="nav-item">
                <Link
                  className={`nav-link px-3 py-2 ${isHistoryView ? 'active bg-primary text-white rounded' : ''}`}
                  to={`${basePath}/history`}
                  onClick={closeMenu}
                >
                  <i className="bi bi-clock-history me-1"></i> {t('history.title')}
                </Link>
              </li>

              {/* Counter Settings Link - Only for users with management access */}
              {hasManageAccess && (
                <li className="nav-item">
                  <Link
                    className={`nav-link px-3 py-2 ${isCounterSettingsView ? 'active bg-primary text-white rounded' : ''}`}
                    to={`${basePath}/counter-settings`}
                    onClick={closeMenu}
                  >
                    <i className="bi bi-gear me-1"></i> {t('settings.counterSettings')}
                  </Link>
                </li>
              )}
            </ul>
          </div>
        </div>
      </nav>
      
      {/* URL display section - as a separate section below navbar */}
      {isOwnerUrl && (
        <div className="bg-body-tertiary border-bottom py-2 mb-3">
          <div className="container">
            <div className="d-flex flex-column gap-2">
              {/* Owner URL */}
              <div className="d-flex align-items-center">
                <span className="badge bg-danger me-2">{t('counter.ownerUrl')}</span>
                <div className="input-group input-group-sm flex-grow-1">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={ownerUrl}
                    readOnly
                    aria-label={t('counter.ownerUrl')}
                  />
                  <button
                    className={`btn btn-outline-secondary btn-sm ${ownerCopyFeedback ? 'btn-success' : ''}`}
                    type="button"
                    onClick={() => handleCopy(ownerUrl, true)}
                    aria-label={t('counter.copyOwnerUrl', 'Copy owner URL')}
                    title={t('counter.copyOwnerUrl', 'Copy owner URL')}
                  >
                    <i className={`bi ${ownerCopyFeedback ? 'bi-check' : 'bi-clipboard'}`}></i>
                    <span className="visually-hidden">{ownerCopyFeedback ? t('counter.copied', 'Copied!') : t('counter.copy', 'Copy')}</span>
                  </button>
                </div>
              </div>

              {/* Public URL */}
              <div className="d-flex align-items-center">
                <span className="badge bg-success me-2">{t('counter.publicUrl')}</span>
                <div className="input-group input-group-sm flex-grow-1">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={publicUrl}
                    readOnly
                    aria-label={t('counter.publicUrl')}
                  />
                  <button
                    className={`btn btn-outline-secondary btn-sm ${publicCopyFeedback ? 'btn-success' : ''}`}
                    type="button"
                    onClick={() => handleCopy(publicUrl, false)}
                    aria-label={t('counter.copyPublicUrl', 'Copy public URL')}
                    title={t('counter.copyPublicUrl', 'Copy public URL')}
                  >
                    <i className={`bi ${publicCopyFeedback ? 'bi-check' : 'bi-clipboard'}`}></i>
                    <span className="visually-hidden">{publicCopyFeedback ? t('counter.copied', 'Copied!') : t('counter.copy', 'Copy')}</span>
                  </button>
                </div>
              </div>
              
              <div className="mt-1">
                <small className="text-body-secondary">
                  {isLoggedIn 
                    ? t('counter.shareUrlLoggedIn', 'Share Public URL for viewing and tapping. Use Owner URL to make further edits to your counter. Owner URL will only work with your login information.')
                    : t('counter.shareUrl', 'Share Public URL for viewing and tapping. Keep Owner URL safe - without it you can\'t edit and if lost, access is permanently gone.')
                  }
                </small>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SubNavbar; 